import { Tag } from "@appkit4/react-components";

import { formatDisplayDateValue } from "../../../utils/dateDisplay";
import { ClientTable, Tabs } from "../../shared";
import {
  BulletList,
  DetailAccordionGroup,
  DetailAccordionSection,
  DetailKeyMessage,
  DetailMetadataStrip,
  DetailModalShell,
  DetailStatusBadge,
  DetailSubsection,
  EvidenceReferenceList,
  LinkedItems,
  PriorityBadge,
  StatusBadge,
} from "../AiInsightsRaidDetailParts";
import { getTagColor } from "../aiInsightTagColor";
import type { DecisionsTopItemDto } from "./AiInsightsDecisionsTypes";

export type AiInsightsDecisionsDetailModalProps = {
  /** The decision card the user clicked; drives every field rendered in the modal. */
  item: DecisionsTopItemDto;
  lastUpdatedAt?: string | null;
  /** Closes the modal (backdrop click, close button, or Escape). */
  onClose: () => void;
};

/** Single-row overview table shown near the top of the modal. */
interface DecisionOverviewRow {
  decisionId: string;
  owner: string;
  priority: string;
  status: string;
}

/** Maps a tolerant boolean field to a display "Yes"/"No", falling back when absent. */
function formatBoolean(value?: boolean | null) {
  if (value == null) {
    return "Not available";
  }

  return value ? "Yes" : "No";
}

/** Renders a list of AppKit Tag components, or a fallback message when empty. */
function TagList({ items, emptyText = "Not available" }: { items: string[]; emptyText?: string }) {
  if (items.length === 0) {
    return <p className="global-insights-overlay__risk-detail-text">{emptyText}</p>;
  }

  return (
    <div className="global-insights-overlay__risk-detail-chip-list">
      {items.map((item, index) => (
        <Tag
          key={`${item}-${index}`}
          label={item}
          type="outlined"
          size="sm"
          closable={false}
          color={getTagColor(item)}
        />
      ))}
    </div>
  );
}

/**
 * Popup modal shown when a user clicks a "Top decisions" card, surfacing the
 * fuller LLM-provided detail for that single decision: an overview table,
 * the specific decision required and why, timing assessment, impact if not
 * decided on time, status currency, decision readiness, and linked items.
 * Mirrors AiInsightsRaidAssumptionsDetailModal / AiInsightsRaidRisksIssuesDetailModal's
 * general layout, surfacing only the fields the decisions insight returns.
 */
export function AiInsightsDecisionsDetailModal({
  item,
  lastUpdatedAt,
  onClose,
}: AiInsightsDecisionsDetailModalProps) {
  const overviewRow: DecisionOverviewRow = {
    decisionId: item.decision_id ?? "Not available",
    owner: item.owner ?? "Not available",
    priority: item.attention_priority ?? "Not available",
    status: item.status ?? "Not available",
  };

  const affectedActivitiesOrMilestones = Array.isArray(
    item.impact_if_not_decided_on_time?.affected_activities_or_milestones
  )
    ? item.impact_if_not_decided_on_time.affected_activities_or_milestones
    : [];
  const affectedWorkstreams = Array.isArray(
    item.impact_if_not_decided_on_time?.affected_workstreams
  )
    ? item.impact_if_not_decided_on_time.affected_workstreams
    : [];
  const affectedObjectives = Array.isArray(
    item.impact_if_not_decided_on_time?.affected_objectives_or_value
  )
    ? item.impact_if_not_decided_on_time.affected_objectives_or_value
    : [];
  const contradictoryEvidenceIds = Array.isArray(
    item.status_currency_assessment?.contradictory_evidence_ids
  )
    ? item.status_currency_assessment.contradictory_evidence_ids
    : [];
  const missingInputsOrBlockers = Array.isArray(item.decision_readiness?.missing_inputs_or_blockers)
    ? item.decision_readiness.missing_inputs_or_blockers
    : [];
  const availableInputs = Array.isArray(item.decision_readiness?.available_inputs)
    ? item.decision_readiness.available_inputs
    : [];
  const linkedItems = Array.isArray(item.linked_items) ? item.linked_items : [];

  interface StatusCurrencyRow {
    lastUpdateDate: string;
    updateAppearsStale: string;
    assessment: string;
    statusReviewNeeded: string;
  }

  const statusCurrencyRows: StatusCurrencyRow[] = [
    {
      lastUpdateDate: formatDisplayDateValue(item.status_currency_assessment?.last_update_date, {
        fallback: "Not available",
      }),
      updateAppearsStale: formatBoolean(item.status_currency_assessment?.update_appears_stale),
      assessment: item.status_currency_assessment?.assessment ?? "Not available",
      statusReviewNeeded: formatBoolean(item.status_currency_assessment?.status_review_needed),
    },
  ];

  return (
    <DetailModalShell
      title={item.title ?? "Not available"}
      onClose={onClose}
      lastUpdatedAt={lastUpdatedAt}
    >
      <DetailMetadataStrip
        items={[
          { label: "Decision ID", value: overviewRow.decisionId, provenance: "Source record" },
          { label: "Owner", value: overviewRow.owner, provenance: "Source record" },
          {
            label: "Priority",
            value: <PriorityBadge value={overviewRow.priority} />,
            provenance: "AI assessed",
          },
          {
            label: "Status",
            value: <StatusBadge value={overviewRow.status} />,
            provenance: "AI assessed",
          },
        ]}
      />

      {item.decision_required !== item.title && (
        <DetailKeyMessage label="Decision required">
          {item.decision_required ?? "Not available"}
        </DetailKeyMessage>
      )}

      <Tabs
        variant="underline"
        className="global-insights-overlay__risk-detail-tabs"
        items={[
          {
            key: "decision",
            label: "Decision",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection
                  heading="Timing assessment"
                  aiGenerated
                  itemKey="timing-assessment"
                  statusBadge={<DetailStatusBadge value={item.timing_assessment?.classification} />}
                >
                  <DetailSubsection label="Days to/from due date">
                    <p className="global-insights-overlay__risk-detail-text">
                      {item.timing_assessment?.days_to_or_from_due ?? "Not available"}
                    </p>
                  </DetailSubsection>
                  <DetailSubsection label="Explanation">
                    <p className="global-insights-overlay__risk-detail-text">
                      {item.timing_assessment?.explanation ?? "Not available"}
                    </p>
                  </DetailSubsection>
                </DetailAccordionSection>
                <DetailAccordionSection
                  heading="Why attention is needed now"
                  aiGenerated
                  itemKey="why-attention"
                >
                  <p className="global-insights-overlay__risk-detail-text">
                    {item.why_attention_is_needed_now ?? "Not available"}
                  </p>
                  <DetailSubsection label="Why the decision is needed">
                    <p className="global-insights-overlay__risk-detail-text">
                      {item.why_decision_is_needed ?? "Not available"}
                    </p>
                  </DetailSubsection>
                </DetailAccordionSection>

                <DetailAccordionSection
                  heading="Impact if not decided on time"
                  aiGenerated
                  itemKey="impact"
                >
                  <div className="global-insights-overlay__risk-detail-grey-box">
                    <p className="global-insights-overlay__risk-detail-sublabel">
                      Impact already being observed
                    </p>
                    <p className="global-insights-overlay__risk-detail-text">
                      {item.impact_if_not_decided_on_time?.observed_impact ?? "Not available"}
                    </p>

                    <p className="global-insights-overlay__risk-detail-sublabel">
                      Potential future impact
                    </p>
                    <p className="global-insights-overlay__risk-detail-text">
                      {item.impact_if_not_decided_on_time?.potential_impact ?? "Not available"}
                    </p>
                  </div>

                  <DetailSubsection label="Affected activities">
                    <TagList items={affectedActivitiesOrMilestones} />
                  </DetailSubsection>

                  <DetailSubsection label="Affected workstreams">
                    <TagList items={affectedWorkstreams} />
                  </DetailSubsection>

                  <DetailSubsection label="Affected objectives">
                    <TagList items={affectedObjectives} />
                  </DetailSubsection>
                </DetailAccordionSection>

                <DetailAccordionSection
                  heading="Decision readiness"
                  aiGenerated
                  itemKey="decision-readiness"
                  statusBadge={<DetailStatusBadge value={item.decision_readiness?.assessment} />}
                >
                  <div className="global-insights-overlay__risk-detail-grey-box">
                    <p className="global-insights-overlay__risk-detail-text">
                      {item.decision_readiness?.explanation ?? "Not available"}
                    </p>

                    <div className="global-insights-overlay__risk-detail-two-col-row">
                      <div className="global-insights-overlay__risk-detail-two-col">
                        <p className="global-insights-overlay__risk-detail-sublabel">
                          Missing inputs or blockers
                        </p>
                        <BulletList items={missingInputsOrBlockers} />
                      </div>
                      <div className="global-insights-overlay__risk-detail-two-col">
                        <p className="global-insights-overlay__risk-detail-sublabel">
                          Available inputs
                        </p>
                        <BulletList items={availableInputs} />
                      </div>
                    </div>
                  </div>
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
          {
            key: "record",
            label: "Status & linked records",
            content: (
              <DetailAccordionGroup>
                <DetailAccordionSection
                  heading="Status currency"
                  aiGenerated
                  itemKey="status-currency-tab"
                  statusBadge={
                    <DetailStatusBadge
                      value={
                        item.status_currency_assessment?.status_review_needed === true ||
                        item.status_currency_assessment?.update_appears_stale === true
                          ? "Review needed"
                          : item.status_currency_assessment?.assessment
                      }
                    />
                  }
                >
                  <p className="global-insights-overlay__risk-detail-warning-box">
                    {item.status_currency_assessment?.explanation ?? "Not available"}
                  </p>
                  <ClientTable
                    className="global-insights-overlay__risk-detail-table global-insights-overlay__risk-detail-table--borderless"
                    data={statusCurrencyRows}
                    rowKey={(row) => row.lastUpdateDate}
                    columns={[
                      {
                        id: "lastUpdateDate",
                        header: "Last update date",
                        accessor: "lastUpdateDate",
                      },
                      {
                        id: "updateAppearsStale",
                        header: "Update appears stale",
                        accessor: "updateAppearsStale",
                      },
                      { id: "assessment", header: "Overall assessment", accessor: "assessment" },
                      {
                        id: "statusReviewNeeded",
                        header: "Status needs review",
                        accessor: "statusReviewNeeded",
                      },
                    ]}
                  />
                  <DetailSubsection label="Contradictory evidence ids">
                    <EvidenceReferenceList
                      items={contradictoryEvidenceIds}
                      emptyText="No data available"
                    />
                  </DetailSubsection>
                </DetailAccordionSection>
                <DetailAccordionSection
                  heading="Linked items"
                  itemKey="linked-items-tab"
                  badge={linkedItems.length}
                >
                  <LinkedItems items={linkedItems} />
                </DetailAccordionSection>
              </DetailAccordionGroup>
            ),
          },
        ]}
      />
    </DetailModalShell>
  );
}

export default AiInsightsDecisionsDetailModal;
